import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import { ConfigContext } from "../context/GlobalContext";
import * as actionType from "../context/actions";
import PaginationView from "./PaginationView";
import DataNotFoundView from "./DataNotFoundView";
import { Languages } from "../Utils/Languages";
import { Tooltip } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import CustomerDetailsPopup from "./child/CustomerDetailsPopup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DeleteCustomerLedgerView from "./DeleteCustomerLedgerView";
import { FilterdData } from "../helper/FilterdData";
import { EditCustomer } from "../data";
import Currancy from "../Utils/currency.json";

const CustomerLedgerTableDataLayer = ({ businessData }) => {
    const navigate = useNavigate();
    const { state, dispatch } = useContext(ConfigContext);

    const filterCurrency = state?.CUSTOMER_CURRENCY_FILTER || "All";
    const displayCurrency = filterCurrency !== "All" ? filterCurrency : (state?.SELECTEDCUSTOMER?.currency || state?.CURRENCYCODE || "INR");

    const matchedCurrency = displayCurrency
        ? Currancy.CountryCurrencyResponse.find(
            (c) => c.currencyCode === displayCurrency
        )
        : null;
    const currencySymbol = matchedCurrency ? matchedCurrency.currencytSymbol : state?.SYMBOL;
    const currencyCode = matchedCurrency ? matchedCurrency.currencyCode : state?.CURRENCYCODE;

    const [selectedBusiness, setSelectedBusiness] = useState("null");
    const [matchedCustomers, setMatchedCustomers] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [editLedger, setEditLedger] = useState(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    // Search
    const [searchQuery, setSearchQuery] = useState("");

    // ------------------ Helpers ------------------
    const computeInvoiceAmount = (inv) => {
        if (!inv) return 0;
        const direct = inv.totalAmount ?? inv.total ?? inv.amount;
        if (direct != null) return Number(direct) || 0;
        const itemsSum = Array.isArray(inv.items)
            ? inv?.items.reduce((s, it) => {
                const price = Number(it.price || 0);
                const qty = Number(it.quantity || 0);
                return s + price * qty;
            }, 0)
            : 0;
        const gst = Number(inv.gstAmount || 0);
        const disc = Number(inv.discountAmount || 0);
        return itemsSum + gst - disc;
    };

    const formatDateForExport = (d) => {
        if (!d && d !== 0) return "";
        let dateObj;
        if (!isNaN(Number(d))) dateObj = new Date(Number(d));
        else dateObj = new Date(d);
        if (isNaN(dateObj.getTime())) return "";
        return dateObj.toDateString();
    };

    useEffect(() => {
        if (!state?.SELECTEDCUSTOMER) return;

        const allEntries = [];

        const customerId = String(state.SELECTEDCUSTOMER?.customerId ?? state.SELECTEDCUSTOMER?.uniqueId ?? state.SELECTEDCUSTOMER?.id ?? "");

        const selectedBusinessId = state?.SELECTEDBUSINESS;
        const isAllBusiness =
            !selectedBusinessId || selectedBusinessId === "null";

        const filterCurrency = state?.CUSTOMER_CURRENCY_FILTER || "All";

        // 🔹 Collect ALL invoices (paid + unpaid)
        (state.INVOICES || [])
            .filter((inv) => {
                const matchCustomer =
                    String(inv.customerId ?? "") === customerId;

                const matchBusiness =
                    isAllBusiness ||
                    String(inv.companyId ?? "") === String(selectedBusinessId);

                const matchCurrency =
                    filterCurrency === "All" ||
                    (inv.currency || state?.CURRENCYCODE || "INR") === filterCurrency;

                return matchCustomer && matchBusiness && matchCurrency;
            })
            .forEach((inv) => {
                const invId = String(inv.invoiceId ?? inv.id ?? "");
                const invAmt = computeInvoiceAmount(inv) || 0;

                allEntries.push({
                    id: invId,
                    date: inv.creationDate ?? inv.date,
                    invoiceId: invId,
                    displayInvoiceId:
                        inv.customInvoiceId || inv.invoiceId || inv.id || "-",
                    note: inv.note || "",
                    debit: invAmt,
                    credit: 0,
                    type: "invoice",
                    paymentMode: "",
                    source: "invoice",
                    companyId: inv.companyId ?? "",
                    status:
                        inv.paidFlagKey === true || inv.paidFlagKey === "true"
                            ? "paid"
                            : "unpaid",
                });
            });

        // 🔹 Collect ALL payments (NO skipping)
        (state?.SELECTEDCUSTOMER?.paymentsReceived || []).forEach((entry) => {
            const entryInvId = String(entry.invoiceId ?? "");

            // 🔍 Find invoice (for business + display)
            const matchedInv = (state.INVOICES || []).find(
                (inv) =>
                    String(inv.invoiceId ?? inv.id ?? "") === entryInvId
            );

            const paymentBusinessId =
                matchedInv?.companyId ?? entry.companyId ?? state.SELECTEDCUSTOMER?.companyId ?? "";

            const matchBusiness =
                isAllBusiness ||
                String(paymentBusinessId) === String(selectedBusinessId);

            if (!matchBusiness) return;

            // Currency matching
            const paymentCurrency = entry.currency || matchedInv?.currency || state.SELECTEDCUSTOMER?.currency || state?.CURRENCYCODE || "INR";
            const matchCurrency =
                filterCurrency === "All" ||
                paymentCurrency === filterCurrency;

            if (!matchCurrency) return;

            let displayId = entry.invoiceId ?? "-";

            if (matchedInv) {
                displayId =
                    matchedInv.customInvoiceId ||
                    matchedInv.invoiceId ||
                    matchedInv.id;
            }

            allEntries.push({
                id: entry.id ?? `ld-${Math.random().toString(36).slice(2, 9)}`,
                date: entry.date,
                invoiceId: entry.invoiceId ?? "-",
                displayInvoiceId: displayId,
                note: entry.note ?? "",
                debit: entry.type === "debit" ? Number(entry.amount) : 0,
                credit: entry.type === "credit" ? Number(entry.amount) : 0,
                type: entry.type,
                paymentMode: entry.paymentMode ?? "",
                source: "ledger",
                companyId: paymentBusinessId,
            });
        });

        // ✅ Apply filter
        const filteredEntries = FilterdData(state, allEntries, "date") || [];

        // 🔹 Sort
        const sortedEntries = [...filteredEntries].sort(
            (a, b) => Number(a.date) - Number(b.date)
        );

        // 🔹 Opening balance
        let openingBalance = 0;

        if (sortedEntries.length > 0) {
            const firstDate = Number(sortedEntries[0].date);

            const olderEntries = allEntries.filter(
                (e) => Number(e.date) < firstDate
            );

            openingBalance = olderEntries.reduce(
                (sum, e) => sum + ((e.debit || 0) - (e.credit || 0)),
                0
            );
        }

        // 🔹 Running balance
        let balance = openingBalance;

        const ledgerWithBalance = sortedEntries.map((entry, index) => {
            balance += (entry.debit || 0) - (entry.credit || 0);

            return {
                ...entry,
                srNo: index + 1,
                totalAmount: balance,
            };
        });

        // 🔹 Opening row
        if (openingBalance !== 0) {
            ledgerWithBalance.unshift({
                srNo: 0,
                note: "Opening Balance",
                totalAmount: openingBalance,
            });
        }

        // ✅ UI update
        setMatchedCustomers(ledgerWithBalance);
        setCurrentPage(1);

        const lastBalance =
            ledgerWithBalance.length > 0
                ? ledgerWithBalance[ledgerWithBalance.length - 1].totalAmount
                : 0;

        const updatedCustomer = {
            ...state.SELECTEDCUSTOMER,
            totalAmount: lastBalance,
        };

        EditCustomer(state, dispatch, updatedCustomer);

    }, [
        state?.SELECTEDCUSTOMER,
        state?.SELECTEDBUSINESS,
        state?.INVOICES,
        state?.ACTIVEFILTER,
        state?.CUSTOMER_CURRENCY_FILTER,
    ]);
    // ------------------ Row click ------------------
    const OnClickOfRow = (row) => {
        if (!row) return;
        dispatch({ type: actionType.SELECTEDCUSTOMER, payload: row });
        navigate("/customer-view");
    };

    // ------------------ Columns ------------------
    const columns = [
        {
            dataField: "srNo",
            text: "No",
            headerAlign: "center",
            align: "center",
            sort: true,
        },
        {
            dataField: "date",
            text: Languages[state?.LANGUAGE]?.issued_date || "Date",
            formatter: (cell) => formatDateForExport(cell),
            sort: true,
            style: { minWidth: "100px" },
            sortCaret: (order) => (
                <span className="d-inline-flex align-items-center ms-2">
                    {!order ? (
                        <Icon icon="mdi:swap-vertical" className="text-muted" />
                    ) : order === "asc" ? (
                        <Icon icon="mdi:arrow-up-thick" className="text-muted" />
                    ) : (
                        <Icon icon="mdi:arrow-down-thick" className="text-muted" />
                    )}
                </span>
            ),
        },
        { dataField: "displayInvoiceId", text: "Invoice ID" },
        { dataField: "paymentMode", text: "Payment Mode" },
        { dataField: "note", text: "Description" },
        {
            dataField: "debit",
            text: "Debit",
            formatter: (cell) => (cell ? `${currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `}${cell}` : ""),
        },
        {
            dataField: "credit",
            text: "Credit",
            formatter: (cell) => (cell ? `${currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `}${cell}` : ""),
        },
        {
            dataField: "totalAmount",
            text: "Balance",
            formatter: (cell) =>
                cell ? `${currencySymbol === "$" ? `${currencyCode} ${currencySymbol} ` : `${currencySymbol} `}${Number(cell).toFixed(2)}` : "",
        },

        {
            dataField: "action",
            text: "Action",
            formatter: (_, row) => {
                const hasInvoice = row.invoiceId && row.invoiceId !== "-";
                return (
                    <div className="d-flex align-items-center gap-2">
                        {hasInvoice && (
                            <Link
                                onClick={() => dispatch({ type: actionType.SELECTEDINVOICE, payload: row.invoiceId })}
                                to="/invoice-preview"
                                className="w-32-px h-32-px bg-primary-light text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center"
                                data-bs-toggle="tooltip"
                                data-bs-placement="top"
                                data-bs-title="View Invoice"
                            >
                                <Icon icon="iconamoon:eye-light" />
                            </Link>
                        )}

                        {row?.type === "credit" && (
                            <>
                                <Link
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setEditLedger(row);
                                        setShowPaymentModal(true);
                                    }}
                                    to="#"
                                    className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                                    data-bs-toggle="tooltip"
                                    data-bs-placement="top"
                                    data-bs-title="Edit Payment"
                                >
                                    <Icon icon="lucide:edit" />
                                </Link>

                                <Link to="#" className="me-2">
                                    <DeleteCustomerLedgerView
                                        customerId={state?.SELECTEDCUSTOMER?.uniqueId}
                                        ledgerId={row?.id}
                                    />
                                </Link>
                            </>
                        )}
                    </div>
                );
            },
        },
    ];

    const exportColumns = columns.filter((c) => c.dataField !== "action");

    // ------------------ Export ------------------
    const exportToExcel = (data) => {
        const selectedBusinessData = state?.BUSINESSES?.find(
            (b) => String(b.companyId) === String(state.SELECTEDBUSINESS)
        );
        const businessName = selectedBusinessData?.companyName || "N/A";
        const customerName = state?.SELECTEDCUSTOMER?.customer || "N/A";

        // ------------------ Totals ------------------
        let totalDebit = 0;
        let totalCredit = 0;
        data.forEach((row) => {
            if (row.srNo) {
                totalDebit += Number(row.debit || 0);
                totalCredit += Number(row.credit || 0);
            }
        });
        const periodTotal = totalDebit - totalCredit;
        const openingBalance = data[0]?.srNo ? 0 : Number(data[0]?.totalAmount || 0);
        const closingBalance = data.length > 0 ? Number(data[data.length - 1]?.totalAmount || 0) : openingBalance;

        // ------------------ Worksheet Rows ------------------
        const aoa = [
            ["Customer Ledger"],
            ["From:", businessName],
            ["Customer:", customerName],
            ["Printed:", new Date().toLocaleString()],
            [],
            ["Date", "Payment Mode", "Description", "Doc No", "Debit", "Credit", "Balance"],
        ];

        // Add Transaction Data
        data.filter(r => r.srNo).forEach((row) => {
            aoa.push([
                formatDateForExport(row.date),
                row.paymentMode || "",
                (row.note || row.description || "").replace(/\n/g, " ").trim(),
                row.displayInvoiceId || "-",
                row.debit ? Number(row.debit) : "",
                row.credit ? Number(row.credit) : "",
                Number(row.totalAmount || 0)
            ]);
        });

        // Add Summary Footer
        aoa.push([]);
        aoa.push(["", "", "", "Opening Balance:", openingBalance]);
        aoa.push(["", "", "", "Total During Period:", periodTotal]);
        aoa.push(["", "", "", "Closing Balance:", closingBalance]);

        const worksheet = XLSX.utils.aoa_to_sheet(aoa);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");

        const fileName = `Ledger-${customerName.replace(/\s+/g, "_")}`;
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, `${fileName}.xlsx`);
    };

    const exportToPDF = (data) => {
        const doc = new jsPDF();

        // ------------------ Helpers ------------------
        const cleanText = (text) => (text ? text.replace(/\n/g, " ").trim() : "");

        const truncateText = (text, max = 80) => {
            if (!text) return "";
            return text.length > max ? text.substring(0, max) + "..." : text;
        };

        const formatCurrency = (num) => {
            const sym = currencySymbol === "$" ? `${currencyCode} ${currencySymbol}` : currencySymbol;
            return `${sym} ${Number(num || 0).toFixed(2)}`;
        };

        // ------------------ Data ------------------
        const selectedBusinessData = state?.BUSINESSES?.find(
            (b) => String(b.companyId) === String(state.SELECTEDBUSINESS)
        );

        const businessName = selectedBusinessData?.companyName || "N/A";
        const customerName = state?.SELECTEDCUSTOMER?.customer || "N/A";
        // ------------------ Totals ------------------
        let totalDebit = 0;
        let totalCredit = 0;

        data.forEach((row) => {
            if (row.srNo) {
                totalDebit += Number(row.debit || 0);
                totalCredit += Number(row.credit || 0);
            }
        });

        const periodTotal = totalDebit - totalCredit;

        const openingBalance = data[0]?.srNo
            ? 0
            : Number(data[0]?.totalAmount || 0);

        const closingBalance =
            data.length > 0
                ? Number(data[data.length - 1]?.totalAmount || 0)
                : openingBalance;

        // ------------------ Header ------------------
        const drawHeader = (d) => {
            const pageWidth = doc.internal.pageSize.width;

            doc.setFontSize(14);
            doc.text(`Customer Ledger`, 14, 15);

            doc.setFontSize(10);
            doc.text(`From: ${businessName}`, 14, 22);

            doc.setFontSize(9);
            doc.text(`Customer: ${customerName}`, 14, 30);

            doc.text(`Page: ${d.pageNumber}`, pageWidth - 14, 22, {
                align: "right",
            });

            doc.text(
                `Printed: ${formatDateForExport(new Date())}`,
                pageWidth - 14,
                28,
                { align: "right" }
            );

            doc.line(14, 35, pageWidth - 14, 35);
        };

        // ------------------ Table ------------------
        const head = [
            [
                "Date",
                "Payment Mode",
                "Description",
                "Doc No",
                "Debit",
                "Credit",
                "Balance",
            ],
        ];

        const body = data
            .filter((r) => r.srNo)
            .map((row) => [
                formatDateForExport(row.date),
                row.paymentMode || "",
                truncateText(cleanText(row.note || row.description)),
                row.displayInvoiceId || "-",
                row.debit ? formatCurrency(row.debit) : "",
                row.credit ? formatCurrency(row.credit) : "",
                formatCurrency(row.totalAmount),
            ]);

        autoTable(doc, {
            head,
            body,
            startY: 40,
            theme: "grid", // ✅ clean table
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: "linebreak", // ✅ wrap text properly
            },
            headStyles: {
                fillColor: [230, 230, 230],
                textColor: 20,
                fontStyle: "bold",
            },
            columnStyles: {
                0: { cellWidth: 22 }, // Date
                1: { cellWidth: 18 }, // Mode
                2: { cellWidth: 60 }, // ✅ Description FIX
                3: { cellWidth: 28 }, // Doc
                4: { halign: "right", cellWidth: 18 },
                5: { halign: "right", cellWidth: 18 },
                6: { halign: "right", cellWidth: 20 },
            },
            margin: { left: 14, right: 14 },
            didDrawPage: drawHeader,
        });

        // ------------------ Footer ------------------
        let lastY = doc.lastAutoTable.finalY + 10;
        const pageWidth = doc.internal.pageSize.width;

        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");

        doc.text(
            `Total: ${formatCurrency(periodTotal)}`,
            pageWidth - 14,
            lastY,
            { align: "right" }
        );

        lastY += 6;

        doc.text(
            `Closing Balance: ${formatCurrency(closingBalance)}`,
            pageWidth - 14,
            lastY,
            { align: "right" }
        );

        // ------------------ Save ------------------
        const fileName = `Ledger-${customerName.replace(/\s+/g, "_")}`;
        doc.save(`${fileName}.pdf`);
    };

    // ------------------ Search + Pagination ------------------
    const filteredData = useMemo(() => {
        const q = (searchQuery || "").trim().toLowerCase();
        if (!q) return matchedCustomers;
        return matchedCustomers.filter((entry) => {
            const hay = `${entry.srNo || ""} ${entry.displayInvoiceId || ""} ${entry.note || ""
                } ${entry.paymentMode || ""} ${entry.debit || ""} ${entry.credit || ""} ${entry.totalAmount || ""
                } ${formatDateForExport(entry.date)}`.toLowerCase();
            return hay.includes(q);
        });
    }, [matchedCustomers, searchQuery]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(start, start + itemsPerPage);
    }, [filteredData, currentPage, itemsPerPage]);

    // Tooltips refresh
    useEffect(() => {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = Array.from(tooltipTriggerList).map(
            (el) => new Tooltip(el)
        );
        return () => tooltipList.forEach((t) => t.dispose());
    }, [paginatedData]);

        return (
        <div className="card">
            <div className="card-body">
                {/* Top controls */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <input
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder={Languages[state?.LANGUAGE]?.search_customer || "Search..."}
                        className="form-control w-auto"
                    />

                    <div className="d-flex align-items-center gap-2">
                        <button
                            onClick={() => {
                                if (!state?.USERID) {
                                    dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                    return;
                                }
                                exportToExcel(paginatedData);
                            }}
                            className="border btn-sm border-success-600 bg-hover-success-200 text-success-600 text-md px-40 py-6 radius-8"
                        >
                            <i className="ri-file-excel-2-line" /> Export Excel
                        </button>

                        <button
                            onClick={() => {
                                if (!state?.USERID) {
                                    dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                    return;
                                }
                                exportToPDF(paginatedData);
                            }}
                            className="border btn-sm border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-6 radius-8"
                        >
                            <i className="ri-file-pdf-2-line" /> Export PDF
                        </button>


                        <button
                            onClick={() => {
                                if (!state?.USERID) {
                                    dispatch({ type: actionType.GUESTMODELPOPUP, payload: true });
                                    return;
                                }
                                setShowPaymentModal(true);
                            }}
                            className="btn btn-primary-600 bg-primary border-primary px-18 py-11 d-flex align-items-center"
                        >
                            <i className="ri-add-line" /> <span className="hide-text">Add Payment</span>
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                    <BootstrapTable
                        keyField="id"
                        data={paginatedData}
                        columns={columns}
                        bootstrap4
                        bordered={false}
                        wrapperClasses={`custom-table-border invoice-table ${localStorage.getItem("theme") === "dark"
                            ? "table-dark"
                            : "table-light"
                            }`}
                        defaultSorted={[{ dataField: "srNo", order: "asc" }]}
                        noDataIndication={() => <DataNotFoundView height={400} />}
                        rowStyle={{ cursor: "pointer" }}
                    />
                </div>

                {/* Pagination */}
                {paginatedData?.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center gap-2 entry_per_page">
                            <div className="dropdown">
                                <button
                                    className="btn btn-outline-primary-600 px-18 py-6 dropdown-toggle toggle-icon"
                                    type="button"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    {itemsPerPage}
                                </button>
                                <ul className="dropdown-menu shadow-md">
                                    {[5, 10, 15, 20].map((value) => (
                                        <li key={value}>
                                            <button
                                                className={`dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 ${itemsPerPage === value ? "bg-primary-50" : ""
                                                    }`}
                                                onClick={() => {
                                                    setItemsPerPage(value);
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                {value}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <span className="text-secondary-light">
                                {Languages[state?.LANGUAGE]?.entries_per_page}
                            </span>
                        </div>

                        <div className="ms-auto">
                            <PaginationView
                                currentPage={currentPage}
                                totalPages={totalPages}
                                handlePageChange={setCurrentPage}
                            />
                        </div>
                    </div>
                )}


                <CustomerDetailsPopup
                    show={showPaymentModal}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setEditLedger(null);
                    }}
                    onSave={(customerId, record) => {
                        // save logic
                    }}
                    customer={state?.SELECTEDCUSTOMER}
                    ledgerData={editLedger}
                />

                {/* Disclaimer Note */}
                <div className="mt-3 d-flex align-items-start gap-2 p-3 radius-8"
                    style={{ background: "rgba(var(--bs-warning-rgb, 255,193,7), 0.08)", border: "1px solid rgba(var(--bs-warning-rgb, 255,193,7), 0.3)" }}>
                    <i className="ri-information-line text-warning-600 mt-1" style={{ fontSize: "15px", flexShrink: 0 }} />
                    <p className="mb-0 text-secondary-light" style={{ fontSize: "12.5px", lineHeight: "1.6" }}>
                        <strong className="text-warning-600">Note: </strong>
                        This ledger does not account for individual paid invoices, as their status is displayed separately. Instead, it reflects only the consolidated lump-sum amounts received from the customer.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CustomerLedgerTableDataLayer;
